import { radius, space } from '@/constants/tokens';
import { Image } from 'expo-image';
import { useEffect } from 'react';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { getTokenValue, Text, View, YStack } from 'tamagui';
import PSText from './ui/PSText';

const LOGOS = [
  require('@/assets/images/partner-logos/ather.png'),
  require('@/assets/images/partner-logos/bic.png'),
  require('@/assets/images/partner-logos/comal.png'),
  require('@/assets/images/partner-logos/comet.png'),
  require('@/assets/images/partner-logos/coracle.png'),
  require('@/assets/images/partner-logos/dali.png'),
  require('@/assets/images/partner-logos/foodstories.png'),
  require('@/assets/images/partner-logos/good-craft.png'),
  require('@/assets/images/partner-logos/gorally.png'),
  require('@/assets/images/partner-logos/guerilla.png'),
  require('@/assets/images/partner-logos/kopitiam.png'),
  require('@/assets/images/partner-logos/lavonne.png'),
  require('@/assets/images/partner-logos/mala.png'),
  require('@/assets/images/partner-logos/map.png'),
  require('@/assets/images/partner-logos/middle-room.png'),
  require('@/assets/images/partner-logos/muro.png'),
  require('@/assets/images/partner-logos/naru.png'),
  require('@/assets/images/partner-logos/pcl.png'),
  require('@/assets/images/partner-logos/pint.png'),
  require('@/assets/images/partner-logos/praxis.png'),
  require('@/assets/images/partner-logos/science-gallery.png'),
  require('@/assets/images/partner-logos/soka.png'),
  require('@/assets/images/partner-logos/speedx.png'),
  require('@/assets/images/partner-logos/spirit-forward.png'),
  require('@/assets/images/partner-logos/subko.png'),
  require('@/assets/images/partner-logos/ultrahuman.png'),
  require('@/assets/images/partner-logos/una.png'),
];

const DIAMETER = 72;
// Points a second, so a longer partner list scrolls for longer rather than faster.
const SPEED = 100;

export default function PartnerMarquee() {
  // The row is a plain RN style (a reanimated view), so the space token is
  // bridged — in render, never at module scope (see PSSkeleton).
  const gap = getTokenValue('$space.md');
  // One copy of the row. It's rendered twice and slides by exactly this much, so
  // the second copy lands where the first started and the seam never shows.
  const loopWidth = LOGOS.length * (DIAMETER + gap);
  const offset = useSharedValue(0);

  useEffect(() => {
    offset.value = withRepeat(
      withTiming(-loopWidth, {
        duration: (loopWidth / SPEED) * 1000,
        // Linear, or the row would visibly slow down at each loop boundary.
        easing: Easing.linear,
      }),
      -1,
      false
    );
    return () => cancelAnimation(offset);
  }, [offset, loopWidth]);

  const marquee = useAnimatedStyle(() => ({
    transform: [{ translateX: offset.value }],
  }));

  return (
    <YStack py="$lg" gap="$lg" mx={-space.lg} overflow="hidden">
      <YStack px="$lg">
        <Text color="$textWhite" fontSize={'$large'} lineHeight={'$large'}>
          We hang with the cool kids,
        </Text>
        <Text
          fontWeight="$semibold"
          color="$brandSecondary"
          fontSize={'$xxl'}
          lineHeight={'$large'}
        >
          come join us!
        </Text>
        <PSText.Body2 color="$textWhiteMuted" my={'$space.sm'}>
          Meet the venues that make Access Pass worth having.
        </PSText.Body2>
      </YStack>

      {/* Inset on the left only: the row runs off the right edge and the block
          clips it — that overflow is the marquee. */}
      <View pl="$lg">
        <Animated.View style={[{ flexDirection: 'row', gap }, marquee]}>
          {[...LOGOS, ...LOGOS].map((logo, index) => (
            <Image
              key={index}
              source={logo}
              style={{
                width: DIAMETER,
                height: DIAMETER,
                borderRadius: radius.full,
              }}
              contentFit="cover"
            />
          ))}
        </Animated.View>
      </View>
    </YStack>
  );
}
